import { createContext, useMemo, useReducer } from "react"
import PropTypes from "prop-types"
import { usePreferences } from "hooks/use-preferences"
import { produce } from "immer"
import { MESSAGE_ROLES } from "../helpers/constants"
import { INSTRUMENT_METADATA_SHAPE, MODEL_METADATA_SHAPE, POSITION_SET_SHAPE } from "../helpers/shapes"

export const AiTeammateContext = createContext()

const PREFERENCE_SUBTYPE = "ai-teammate"

const PREFERENCE_FIELDS = [
  "threadId",
  "drawerWidth",
]

const DEFAULT_STATE = {
  threadId: null,
  messages: [],
  error: null,
  userMessageInputOverride: "",
}

function reducer(current, action) {
  return produce(current, draft => {
    switch (action.type) {
      case "addMessage":
        draft.messages.push(action.message)
        break
      case "removeLastUserMessage": {
        const lastUserMessageIdx = draft.messages.findLastIndex(message => message.role === MESSAGE_ROLES.USER)
        if (lastUserMessageIdx !== -1) {
          draft.messages.splice(lastUserMessageIdx, 1)
        }
        break
      }
      case "resetContext":
        Object.assign(draft, DEFAULT_STATE)
        break
      case "resetUserMessageInput":
        draft.userMessageInputOverride = ""
        break
      case "setDrawerWidth":
        draft.drawerWidth = action.drawerWidth
        break
      case "setError":
        draft.error = action.error
        break
      case "setMessages":
        draft.messages = action.messages
        break
      case "setPreferences":
        Object.assign(draft, action.preferences)
        break
      case "setThreadId":
        draft.threadId = action.threadId
        break
      case "setUserMessageInputOverride":
        draft.userMessageInputOverride = action.userMessageInputOverride
        break
    }
  })
}

function addMessage(message) {
  return { type: "addMessage", message }
}

function removeLastUserMessage() {
  return { type: "removeLastUserMessage" }
}

function resetContext() {
  return { type: "resetContext" }
}

function resetUserMessageInput() {
  return { type: "resetUserMessageInput" }
}

function setDrawerWidth(drawerWidth) {
  return { type: "setDrawerWidth", drawerWidth }
}

function setError(error) {
  return { type: "setError", error }
}

function setMessages(messages) {
  return { type: "setMessages", messages }
}

function setPreferences(preferences) {
  return { type: "setPreferences", preferences }
}

function setThreadId(threadId) {
  return { type: "setThreadId", threadId }
}

function setUserMessageInputOverride(userMessageInputOverride) {
  return { type: "setUserMessageInputOverride", userMessageInputOverride }
}

export function AiTeammateProvider({ children, ...otherProps }) {
  const [state, dispatch] = useReducer(reducer, DEFAULT_STATE)

  const isPreferencesLoaded = usePreferences(
    PREFERENCE_SUBTYPE,
    PREFERENCE_FIELDS,
    state,
    dispatch,
    setPreferences,
  )

  const contextUpdaters = useMemo(() => ({
    addMessage: (...args) => dispatch(addMessage(...args)),
    removeLastUserMessage: (...args) => dispatch(removeLastUserMessage(...args)),
    resetContext: (...args) => dispatch(resetContext(...args)),
    resetUserMessageInput: (...args) => dispatch(resetUserMessageInput(...args)),
    setDrawerWidth: (...args) => dispatch(setDrawerWidth(...args)),
    setError: (...args) => dispatch(setError(...args)),
    setMessages: (...args) => dispatch(setMessages(...args)),
    setPreferences: (...args) => dispatch(setPreferences(...args)),
    setThreadId: (...args) => dispatch(setThreadId(...args)),
    setUserMessageInputOverride: (...args) => dispatch(setUserMessageInputOverride(...args)),
  }), [dispatch])

  const contextValue = useMemo(() => ({
    ...state,
    ...contextUpdaters,
    ...otherProps,
    isPreferencesLoaded,
  }), [state, contextUpdaters, otherProps, isPreferencesLoaded])

  return <AiTeammateContext.Provider value={contextValue}>
    {children}
  </AiTeammateContext.Provider>
}

AiTeammateProvider.propTypes = {
  modelId: PropTypes.string,
  instrument: POSITION_SET_SHAPE,
  base: POSITION_SET_SHAPE,
  modelMetadata: MODEL_METADATA_SHAPE,
  resourceMetadata: INSTRUMENT_METADATA_SHAPE,
  benchmarkMetadata: INSTRUMENT_METADATA_SHAPE,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  currency: PropTypes.string,
}
